import { useCallback, useEffect, useState } from 'react';
import {
  clearPreviewIdentity,
  hasStoredPreviewIdentity,
  hasPreviewIdentity,
  persistPreviewIdentity,
  PREVIEW_IDENTITY_EVENT,
  readPreviewIdentity,
  resolvePreviewIdentityLabel,
  TPreviewIdentity,
  TPreviewIdentityInput,
} from '../utils/previewIdentity';

export default function usePreviewIdentity() {
  const [identity, setIdentity] = useState<TPreviewIdentity>(() => readPreviewIdentity());

  useEffect(() => {
    const handleIdentityChanged = (event: Event) => {
      const nextIdentity = ((event as CustomEvent<TPreviewIdentity>).detail as TPreviewIdentity | undefined) ?? { name: '', email: '' };
      setIdentity({
        name: nextIdentity.name?.trim() ?? '',
        email: nextIdentity.email?.trim() ?? '',
      });
    };

    window.addEventListener(PREVIEW_IDENTITY_EVENT, handleIdentityChanged);
    return () => {
      window.removeEventListener(PREVIEW_IDENTITY_EVENT, handleIdentityChanged);
    };
  }, []);

  const saveIdentity = useCallback((input: TPreviewIdentityInput) => {
    const nextIdentity = persistPreviewIdentity(input);
    setIdentity(nextIdentity);
    return nextIdentity;
  }, []);

  const removeIdentity = useCallback(() => {
    const clearedIdentity = clearPreviewIdentity();
    setIdentity(clearedIdentity);
    return clearedIdentity;
  }, []);

  return {
    identity,
    hasIdentity: hasPreviewIdentity(identity),
    hasStoredIdentity: hasStoredPreviewIdentity(identity),
    identityLabel: resolvePreviewIdentityLabel(identity),
    saveIdentity,
    clearIdentity: removeIdentity,
  };
}
